import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';

// Stable short ID from article title (first 8 chars of simple hash)
function articleId(title) {
  if (!title) return 'x';
  let h = 0;
  for (let i = 0; i < title.length; i++) { h = (Math.imul(31, h) + title.charCodeAt(i)) | 0; }
  return Math.abs(h).toString(36).slice(0, 8);
}
function articleUrl(article) {
  return `https://knowfi.io/market-pulse?article=${articleId(article.title || article.headline || '')}`;
}

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const REFRESH_SECONDS = 90;
const EDITORIAL_REFRESH = 180; // 3 min frontend poll (backend generates every 3h)

// Source badge colors
const SOURCE_COLORS = {
  'Yahoo Finance':     '#a78bfa',
  'CNBC':             '#22c55e',
  'MarketWatch':      '#f472b6',
  'Wall Street Journal': '#38bdf8',
  'Bloomberg':        '#38bdf8',
  'Fortune':          '#f59e0b',
  "Investor's Business Daily": '#34d399',
  'Reuters':          '#fb923c',
  'Business Insider': '#e879f9',
  'CoinDesk':         '#f59e0b',
  'CoinTelegraph':    '#22c55e',
  'Decrypt':          '#818cf8',
  'Google News':      '#64748b',
  'NerdWallet':       '#34d399',
};

function sourceColor(source) {
  if (!source) return '#64748b';
  for (const [k, v] of Object.entries(SOURCE_COLORS)) {
    if (source.toLowerCase().includes(k.toLowerCase())) return v;
  }
  return '#64748b';
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function isBreaking(dateStr) {
  if (!dateStr) return false;
  return (Date.now() - new Date(dateStr)) / 1000 < 3600;
}

function isNew(dateStr) {
  if (!dateStr) return false;
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  return diff >= 3600 && diff < 10800;
}

function buildShareText(article) {
  const headline = article.title || article.headline || '';
  const tag = '#KnowFi #Finance #Investing';
  return `${headline}\n\n${tag}`;
}

function ShareButtons({ article }) {
  const [copied, setCopied] = useState(false);
  const permaUrl = articleUrl(article);
  const text = buildShareText(article);
  const url = encodeURIComponent(permaUrl);
  const encoded = encodeURIComponent(`${text}\n\n${permaUrl}`);

  const copyLink = () => {
    navigator.clipboard.writeText(permaUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={shr.row}>
      <a
        href={`https://twitter.com/intent/tweet?text=${encoded}`}
        target="_blank" rel="noopener noreferrer"
        style={{ ...shr.btn, background: '#1d9bf0' }}
        title="Share on X"
      >𝕏</a>
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${encodeURIComponent(article.title || article.headline || '')}`}
        target="_blank" rel="noopener noreferrer"
        style={{ ...shr.btn, background: '#1877f2' }}
        title="Share on Facebook"
      >f</a>
      <a
        href={`https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${encodeURIComponent(article.title || article.headline || '')}`}
        target="_blank" rel="noopener noreferrer"
        style={{ ...shr.btn, background: '#0a66c2' }}
        title="Share on LinkedIn"
      >in</a>
      <a
        href={`https://reddit.com/submit?url=${url}&title=${encodeURIComponent(article.title || article.headline || '')}`}
        target="_blank" rel="noopener noreferrer"
        style={{ ...shr.btn, background: '#ff4500' }}
        title="Share on Reddit"
      >r/</a>
      <button onClick={copyLink} style={{ ...shr.btn, background: copied ? '#22c55e' : '#334155', border: 'none', cursor: 'pointer' }} title="Copy link">
        {copied ? '✓' : '🔗'}
      </button>
    </div>
  );
}

const shr = {
  row: { display: 'flex', gap: '6px', marginTop: '12px', flexWrap: 'wrap' },
  btn: { color: '#fff', fontWeight: '700', fontSize: '11px', padding: '5px 10px', borderRadius: '6px', textDecoration: 'none', letterSpacing: '0.3px', lineHeight: '1' },
};

const CAT_COLORS = {
  Markets:          '#38bdf8',
  Economy:          '#a78bfa',
  Crypto:           '#f59e0b',
  'Personal Finance': '#34d399',
};

function EditorialCard({ article, hero = false }) {
  const [expanded, setExpanded] = useState(hero); // hero always open, others start collapsed
  const [hovered, setHovered]   = useState(false);
  const color = CAT_COLORS[article.category] || '#38bdf8';
  const permaUrl = articleUrl(article);
  // X share uses the hook — it's the scroll-stopping line; headline + hook = perfect tweet
  const tweetBody = article.hook
    ? `${article.hook}\n\n${article.headline}\n\n#KnowFi #Finance #Investing\n${permaUrl}`
    : `${article.headline}\n\n#KnowFi #Finance #Investing\n${permaUrl}`;
  const shareText = encodeURIComponent(tweetBody);
  const timeLabel = article.published_at ? timeAgo(article.published_at) : '';

  return (
    <div
      style={{
        background: hovered ? (hero ? '#1a2744' : '#131f2e') : (hero ? '#0f1e3d' : '#0d1520'),
        border: `1px solid ${hero ? `${color}44` : `${color}22`}`,
        borderRadius: hero ? '20px' : '16px',
        padding: hero ? '32px 32px' : '20px 24px',
        position: 'relative',
        overflow: 'hidden',
        transition: 'background 0.15s, box-shadow 0.15s',
        boxShadow: hovered ? `0 0 32px ${color}14` : 'none',
        marginBottom: hero ? '32px' : '0',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '240px', height: '240px', background: `radial-gradient(ellipse, ${color}10 0%, transparent 70%)`, pointerEvents: 'none' }} />

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {hero && <span style={{ background: '#ef4444', color: '#fff', fontSize: '9px', fontWeight: '800', letterSpacing: '1px', padding: '3px 9px', borderRadius: '20px' }}>⭐ TOP STORY</span>}
        <span style={{ background: `${color}18`, color, fontSize: '10px', fontWeight: '700', padding: '3px 10px', borderRadius: '12px', letterSpacing: '0.5px' }}>{article.category}</span>
        <span style={{ color: '#475569', fontSize: '11px' }}>KnowFi Editorial</span>
        {timeLabel && <span style={{ color: '#334155', fontSize: '11px' }}>· {timeLabel}</span>}
        {article.sources?.length > 0 && <span style={{ color: '#334155', fontSize: '11px' }}>· Sources: {article.sources.join(', ')}</span>}
      </div>

      {/* Headline — clickable to toggle on non-hero */}
      <h2
        onClick={() => !hero && setExpanded(x => !x)}
        style={{ color: '#f8fafc', fontSize: hero ? '22px' : '17px', fontWeight: '800', lineHeight: 1.35, margin: '0 0 10px', letterSpacing: '-0.3px', cursor: hero ? 'default' : 'pointer' }}
      >
        {article.headline}
        {!hero && <span style={{ color: color, fontSize: '13px', marginLeft: '8px', fontWeight: '600' }}>{expanded ? ' ▲' : ' ▼'}</span>}
      </h2>

      {/* Hook — always visible, styled as pull quote */}
      <p style={{ color, fontSize: '14px', fontWeight: '600', lineHeight: 1.55, margin: '0 0 14px', fontStyle: 'italic', borderLeft: `3px solid ${color}`, paddingLeft: '14px' }}>
        {article.hook}
      </p>

      {/* Body — expanded state, formatted as paragraphs */}
      {expanded && (
        <>
          <div style={{ color: '#94a3b8', fontSize: '15px', lineHeight: 1.8, margin: '0 0 20px' }}>
            {(article.body || '').split('\n').filter(p => p.trim()).map((para, i) => {
              // Render **Section Header:** in bold color
              const match = para.match(/^\*\*(.+?)\*\*:?\s*(.*)/);
              if (match) {
                return (
                  <p key={i} style={{ margin: '0 0 12px' }}>
                    <span style={{ color, fontWeight: '700', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{match[1]}: </span>
                    <span>{match[2]}</span>
                  </p>
                );
              }
              return <p key={i} style={{ margin: '0 0 14px' }}>{para}</p>;
            })}
          </div>
          <div style={{ borderTop: `1px solid ${color}20`, marginBottom: '16px' }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
            <span style={{ color: '#475569', fontSize: '12px' }}>By {article.byline || 'KnowFi Editorial Team'} · {article.read_minutes || 3} min read</span>
            <div style={shr.row}>
              <a href={`https://twitter.com/intent/tweet?text=${shareText}`} target="_blank" rel="noopener noreferrer" style={{ ...shr.btn, background: '#1d9bf0' }} title="Share on X">𝕏</a>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://knowfi.io/market-pulse')}&quote=${encodeURIComponent(article.headline)}`} target="_blank" rel="noopener noreferrer" style={{ ...shr.btn, background: '#1877f2' }}>f</a>
              <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent('https://knowfi.io/market-pulse')}&title=${encodeURIComponent(article.headline)}`} target="_blank" rel="noopener noreferrer" style={{ ...shr.btn, background: '#0a66c2' }}>in</a>
              <a href={`https://reddit.com/submit?url=${encodeURIComponent('https://knowfi.io/market-pulse')}&title=${encodeURIComponent(article.headline)}`} target="_blank" rel="noopener noreferrer" style={{ ...shr.btn, background: '#ff4500' }}>r/</a>
            </div>
          </div>
        </>
      )}

      {/* Collapse toggle for non-hero */}
      {!hero && (
        <button
          onClick={() => setExpanded(x => !x)}
          style={{ background: 'none', border: 'none', color: expanded ? '#475569' : color, fontSize: '12px', fontWeight: '700', cursor: 'pointer', padding: '6px 0 0', display: 'block' }}
        >
          {expanded ? '▲ Collapse' : `▼ Read article · ${article.read_minutes || 3} min`}
        </button>
      )}
    </div>
  );
}

function HeroCard({ article }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        background: hovered ? '#1a2744' : '#0f1e3d',
        border: '1px solid rgba(56,189,248,0.35)',
        borderRadius: '20px',
        padding: '28px 32px',
        marginBottom: '28px',
        position: 'relative',
        overflow: 'hidden',
        transition: 'background 0.2s, box-shadow 0.2s',
        boxShadow: hovered ? '0 0 40px rgba(56,189,248,0.15)' : '0 0 20px rgba(56,189,248,0.06)',
        cursor: 'pointer',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Glow accent */}
      <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '260px', height: '260px', background: 'radial-gradient(ellipse, rgba(56,189,248,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <span style={{ background: '#ef4444', color: '#fff', fontSize: '10px', fontWeight: '800', letterSpacing: '1px', padding: '3px 10px', borderRadius: '20px' }}>🔴 TOP STORY</span>
        {isBreaking(article.pubDate) && (
          <span style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', color: '#f87171', fontSize: '10px', fontWeight: '700', padding: '3px 10px', borderRadius: '20px', letterSpacing: '1px' }}>BREAKING</span>
        )}
        <span style={{ background: `${sourceColor(article.source)}22`, color: sourceColor(article.source), fontSize: '11px', fontWeight: '600', padding: '3px 10px', borderRadius: '20px' }}>{article.source}</span>
        <span style={{ color: '#475569', fontSize: '12px' }}>{timeAgo(article.pubDate)}</span>
      </div>

      <a href={article.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
        <h2 style={{ color: '#f8fafc', fontSize: '22px', fontWeight: '800', lineHeight: 1.35, margin: '0 0 10px', letterSpacing: '-0.3px' }}>
          {article.title}
        </h2>
      </a>
      {article.description && (
        <p style={{ color: '#94a3b8', fontSize: '15px', lineHeight: 1.65, margin: '0 0 16px' }}>
          {article.description.replace(/<[^>]*>/g, '').slice(0, 200)}{article.description.length > 200 ? '…' : ''}
        </p>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <a href={article.link} target="_blank" rel="noopener noreferrer"
          style={{ color: '#38bdf8', fontSize: '13px', fontWeight: '600', textDecoration: 'none' }}>
          Read full story →
        </a>
        <ShareButtons article={article} />
      </div>
    </div>
  );
}

function NewsCard({ article, rank }) {
  const [hovered, setHovered] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const breaking = isBreaking(article.pubDate);
  const fresh = isNew(article.pubDate);

  return (
    <div
      style={{
        background: hovered ? '#1a2338' : '#0f1827',
        border: `1px solid ${breaking ? 'rgba(239,68,68,0.3)' : 'rgba(51,65,85,0.8)'}`,
        borderRadius: '14px',
        padding: '18px 20px',
        transition: 'background 0.15s, border-color 0.15s, box-shadow 0.15s',
        boxShadow: hovered ? '0 4px 24px rgba(0,0,0,0.3)' : 'none',
        cursor: 'pointer',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
        {/* Rank */}
        <div style={{ color: '#1e3a5f', fontSize: '22px', fontWeight: '900', lineHeight: 1, minWidth: '28px', paddingTop: '2px', fontVariantNumeric: 'tabular-nums' }}>
          {String(rank).padStart(2, '0')}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Badges row */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            {breaking && (
              <span style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.35)', color: '#f87171', fontSize: '9px', fontWeight: '800', padding: '2px 8px', borderRadius: '20px', letterSpacing: '0.8px' }}>● BREAKING</span>
            )}
            {fresh && !breaking && (
              <span style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', color: '#4ade80', fontSize: '9px', fontWeight: '700', padding: '2px 8px', borderRadius: '20px', letterSpacing: '0.8px' }}>● NEW</span>
            )}
            <span style={{ background: `${sourceColor(article.source)}18`, color: sourceColor(article.source), fontSize: '10px', fontWeight: '600', padding: '2px 8px', borderRadius: '12px' }}>
              {article.source}
            </span>
            <span style={{ color: '#475569', fontSize: '11px' }}>{timeAgo(article.pubDate)}</span>
          </div>

          {/* Headline */}
          <a href={article.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            <h3 style={{ color: hovered ? '#e2e8f0' : '#cbd5e1', fontSize: '15px', fontWeight: '700', lineHeight: 1.4, margin: '0 0 8px', letterSpacing: '-0.1px' }}>
              {article.title}
            </h3>
          </a>

          {/* Expand/collapse snippet */}
          {article.description && (
            <>
              {expanded && (
                <p style={{ color: '#64748b', fontSize: '13px', lineHeight: 1.6, margin: '0 0 10px' }}>
                  {article.description.replace(/<[^>]*>/g, '').slice(0, 250)}{article.description.length > 250 ? '…' : ''}
                </p>
              )}
              <button
                onClick={e => { e.stopPropagation(); setExpanded(x => !x); }}
                style={{ background: 'none', border: 'none', color: '#475569', fontSize: '12px', cursor: 'pointer', padding: '0', marginBottom: expanded ? '10px' : '0' }}
              >
                {expanded ? '▲ less' : '▼ more'}
              </button>
            </>
          )}

          {/* Share row */}
          {expanded && <ShareButtons article={article} />}
          {!expanded && (
            <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
              <a href={article.link} target="_blank" rel="noopener noreferrer"
                style={{ color: '#38bdf8', fontSize: '12px', fontWeight: '600', textDecoration: 'none' }}>
                Read →
              </a>
              <span style={{ color: '#334155' }}>·</span>
              <ShareButtons article={article} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Ticker({ articles }) {
  const ref = useRef(null);
  const text = articles.slice(0, 15).map(a => a.title).join('   ·   ');

  return (
    <div style={{ background: '#0a1628', borderBottom: '1px solid #1e3a5f', overflow: 'hidden', whiteSpace: 'nowrap', padding: '8px 0' }}>
      <div ref={ref} style={{ display: 'inline-block', animation: 'ticker 60s linear infinite', fontSize: '12px', color: '#94a3b8', fontWeight: '500', letterSpacing: '0.2px', paddingRight: '100px' }}>
        📡 LIVE &nbsp;&nbsp; {text} &nbsp;&nbsp;&nbsp;&nbsp; {text}
      </div>
      <style>{`@keyframes ticker { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
    </div>
  );
}

function CountdownBar({ secondsLeft, total }) {
  const pct = (secondsLeft / total) * 100;
  return (
    <div style={{ height: '2px', background: '#1e293b', borderRadius: '1px', overflow: 'hidden', marginBottom: '24px' }}>
      <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, #38bdf8, #818cf8)', transition: 'width 1s linear', borderRadius: '1px' }} />
    </div>
  );
}

const CATEGORIES = [
  { id: 'all',    label: '⚡ All' },
  { id: 'market', label: '📈 Markets' },
  { id: 'crypto', label: '₿ Crypto' },
  { id: 'economy',label: '🏛️ Economy' },
];

const MARKET_KEYWORDS   = ['stock', 's&p', 'nasdaq', 'dow', 'fed', 'rate', 'earnings', 'shares', 'wall street', 'index', 'equity', 'ipo', 'market', 'yield', 'treasury', 'bond'];
const CRYPTO_KEYWORDS   = ['bitcoin', 'btc', 'ethereum', 'eth', 'crypto', 'blockchain', 'defi', 'token', 'nft', 'altcoin', 'binance', 'coinbase', 'solana', 'ripple'];
const ECONOMY_KEYWORDS  = ['inflation', 'gdp', 'jobs', 'unemployment', 'cpi', 'fed', 'interest rate', 'recession', 'economy', 'tariff', 'trade war', 'deficit', 'stimulus', 'pce'];

function filterByCategory(articles, cat) {
  if (cat === 'all') return articles;
  const kws = cat === 'market' ? MARKET_KEYWORDS : cat === 'crypto' ? CRYPTO_KEYWORDS : ECONOMY_KEYWORDS;
  return articles.filter(a => kws.some(kw => (a.title + ' ' + (a.description || '')).toLowerCase().includes(kw)));
}

export default function MarketPulse() {
  const [searchParams] = useSearchParams();
  const [articles, setArticles]         = useState([]);
  const [editorials, setEditorials]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(false);
  const [lastUpdated, setLastUpdated]   = useState(null);
  const [countdown, setCountdown]       = useState(REFRESH_SECONDS);
  const [category, setCategory]         = useState('all');
  const [search, setSearch]             = useState('');
  const [view, setView]                 = useState('editorial'); // 'editorial' | 'live'
  const [highlightId, setHighlightId]   = useState(null);
  const isMobile = window.innerWidth < 768;

  const fetchNews = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/news`);
      const d = await r.json();
      if (d.articles?.length) {
        setArticles(d.articles);
        setLastUpdated(new Date());
        setError(false);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
      setCountdown(REFRESH_SECONDS);
    }
  }, []);

  const fetchEditorial = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/editorial`);
      const d = await r.json();
      if (d.articles?.length) setEditorials(d.articles);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchNews();
    fetchEditorial();
    const t1 = setInterval(fetchNews, REFRESH_SECONDS * 1000);
    const t2 = setInterval(fetchEditorial, EDITORIAL_REFRESH * 1000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, [fetchNews, fetchEditorial]);

  // Scroll to and highlight shared article on load
  const targetArticleId = searchParams.get('article');
  const highlightHeadline = searchParams.get('highlight');
  useEffect(() => {
    // Handle ?highlight=Headline+Text (from CryptoCorner)
    if (highlightHeadline && editorials.length > 0) {
      const found = editorials.find(a => (a.headline || '').toLowerCase() === highlightHeadline.toLowerCase());
      if (found) {
        const aid = articleId(found.headline || '');
        setHighlightId(aid);
        setView('editorial');
        setTimeout(() => {
          const el = document.getElementById(`article-${aid}`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 400);
      }
      return;
    }
    if (!targetArticleId) return;
    const allItems = [...editorials, ...articles];
    const found = allItems.find(a => articleId(a.title || a.headline || '') === targetArticleId);
    if (found) {
      setHighlightId(targetArticleId);
      // Switch to correct tab
      if (found.headline) setView('editorial');
      else setView('live');
      setTimeout(() => {
        const el = document.getElementById(`article-${targetArticleId}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 400);
    }
  }, [targetArticleId, highlightHeadline, articles, editorials]); // eslint-disable-line

  useEffect(() => {
    const tick = setInterval(() => setCountdown(c => c > 0 ? c - 1 : REFRESH_SECONDS), 1000);
    return () => clearInterval(tick);
  }, []);

  const filtered = filterByCategory(articles, category).filter(a => {
    if (!search.trim()) return true;
    return (a.title + ' ' + (a.description || '') + ' ' + (a.source || '')).toLowerCase().includes(search.toLowerCase());
  });

  const hero = filtered[0];
  const rest = filtered.slice(1);

  const formatTime = (d) => d ? d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div style={{ background: '#060d1a', minHeight: '100vh', color: '#f8fafc', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <SEO
        title="Market Pulse — Live Financial Headlines"
        description="The biggest market-moving headlines updated every 90 seconds. Breaking news from CNBC, Bloomberg, WSJ, MarketWatch and more — explained without the jargon."
        keywords="breaking financial news, market news today, stock market headlines, live market news, crypto news, economy news"
      />

      {/* Ticker tape */}
      {articles.length > 0 && <Ticker articles={articles} />}

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: isMobile ? '24px 16px' : '36px 24px' }}>

        {/* Header */}
        <div style={{ marginBottom: '28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ display: 'inline-block', width: '8px', height: '8px', background: '#22c55e', borderRadius: '50%', boxShadow: '0 0 8px #22c55e', animation: 'pulse 2s infinite' }} />
              <span style={{ color: '#22c55e', fontSize: '12px', fontWeight: '700', letterSpacing: '1px' }}>LIVE</span>
              <style>{`@keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }`}</style>
            </div>
            <h1 style={{ color: '#f8fafc', fontSize: isMobile ? '26px' : '32px', fontWeight: '900', margin: 0, letterSpacing: '-0.5px' }}>
              Market Pulse
            </h1>
          </div>
          <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 12px' }}>
            The headlines that move markets — refreshing every 90 seconds
            {lastUpdated && <span style={{ color: '#334155' }}> · last update {formatTime(lastUpdated)}</span>}
          </p>
          <CountdownBar secondsLeft={countdown} total={REFRESH_SECONDS} />

          {/* View toggle */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
            <button onClick={() => setView('editorial')} style={{
              background: view === 'editorial' ? '#38bdf8' : '#0f1827',
              color: view === 'editorial' ? '#060d1a' : '#64748b',
              border: `1px solid ${view === 'editorial' ? '#38bdf8' : '#1e293b'}`,
              borderRadius: '20px', padding: '7px 18px', fontSize: '13px', fontWeight: '800', cursor: 'pointer',
            }}>📝 KnowFi Articles {editorials.length > 0 && `(${editorials.length})`}</button>
            <button onClick={() => setView('live')} style={{
              background: view === 'live' ? '#ef4444' : '#0f1827',
              color: view === 'live' ? '#fff' : '#64748b',
              border: `1px solid ${view === 'live' ? '#ef4444' : '#1e293b'}`,
              borderRadius: '20px', padding: '7px 18px', fontSize: '13px', fontWeight: '800', cursor: 'pointer',
            }}>⚡ Live Feed</button>
          </div>

          {/* Category tabs — only for live feed */}
          {view === 'live' && <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
            {CATEGORIES.map(c => (
              <button
                key={c.id}
                onClick={() => setCategory(c.id)}
                style={{
                  background: category === c.id ? '#38bdf8' : '#0f1827',
                  color: category === c.id ? '#060d1a' : '#64748b',
                  border: `1px solid ${category === c.id ? '#38bdf8' : '#1e293b'}`,
                  borderRadius: '20px',
                  padding: '6px 16px',
                  fontSize: '13px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >{c.label}</button>
            ))}

            {/* Search */}
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search headlines…"
              style={{
                marginLeft: 'auto',
                background: '#0f1827',
                border: '1px solid #1e293b',
                color: '#f8fafc',
                borderRadius: '20px',
                padding: '6px 16px',
                fontSize: '13px',
                outline: 'none',
                width: isMobile ? '100%' : '200px',
              }}
            />
          </div>}

          {/* Stats bar */}
          {!loading && !error && (
            <div style={{ display: 'flex', gap: '20px', padding: '12px 0', borderTop: '1px solid #0f1e3d', borderBottom: '1px solid #0f1e3d', marginBottom: '24px', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '13px', color: '#64748b' }}>
                <span style={{ color: '#38bdf8', fontWeight: '700' }}>{editorials.length}</span> KnowFi articles
              </div>
              <div style={{ fontSize: '13px', color: '#64748b' }}>
                <span style={{ color: '#ef4444', fontWeight: '700' }}>{filtered.filter(a => isBreaking(a.pubDate)).length}</span> breaking raw
              </div>
              <div style={{ fontSize: '13px', color: '#64748b', marginLeft: 'auto' }}>
                Next refresh in <span style={{ color: '#f8fafc', fontWeight: '700' }}>{countdown}s</span>
                <button onClick={() => { fetchNews(); fetchEditorial(); }} style={{ background: 'none', border: 'none', color: '#38bdf8', fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginLeft: '8px' }}>↺ now</button>
              </div>
            </div>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={{ fontSize: '32px', marginBottom: '16px' }}>📡</div>
            <div style={{ color: '#38bdf8', fontSize: '16px', fontWeight: '600' }}>Pulling live headlines…</div>
            <div style={{ color: '#334155', fontSize: '13px', marginTop: '8px' }}>Scanning 18 financial sources</div>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={{ fontSize: '32px', marginBottom: '16px' }}>⚠️</div>
            <div style={{ color: '#ef4444', fontSize: '16px', fontWeight: '600' }}>Couldn't load headlines</div>
            <button onClick={fetchNews} style={{ marginTop: '16px', background: '#38bdf8', color: '#060d1a', border: 'none', borderRadius: '8px', padding: '10px 24px', fontWeight: '700', cursor: 'pointer', fontSize: '14px' }}>
              Try again
            </button>
          </div>
        )}

        {/* Content */}
        {!loading && !error && (
          <>
            {/* EDITORIAL VIEW */}
            {view === 'editorial' && (
              editorials.length > 0 ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', padding: '12px 16px', background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.12)', borderRadius: '12px' }}>
                    <span style={{ fontSize: '20px' }}>📝</span>
                    <div>
                      <div style={{ color: '#f8fafc', fontWeight: '800', fontSize: '14px' }}>KnowFi Editorial</div>
                      <div style={{ color: '#475569', fontSize: '12px' }}>Original articles written by our team, sourced from 18 financial news outlets. Updated every 3 hours.</div>
                    </div>
                  </div>
                  <div id={`article-${articleId(editorials[0].headline || '')}`} style={highlightId === articleId(editorials[0].headline || '') ? { outline: '2px solid #38bdf8', borderRadius: '14px' } : {}}>
                    <EditorialCard article={editorials[0]} hero={true} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {editorials.slice(1).map((a, i) => {
                      const aid = articleId(a.headline || '');
                      return (
                        <div key={a.id || i} id={`article-${aid}`} style={highlightId === aid ? { outline: '2px solid #38bdf8', borderRadius: '14px' } : {}}>
                          <EditorialCard article={a} />
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>✍️</div>
                  <div style={{ color: '#38bdf8', fontWeight: '700', fontSize: '16px' }}>Our editorial team is writing...</div>
                  <div style={{ color: '#475569', fontSize: '13px', marginTop: '8px' }}>First batch of KnowFi articles generates on server start. Check back in a few minutes.</div>
                  <button onClick={() => setView('live')} style={{ marginTop: '16px', background: '#0f1827', border: '1px solid #1e293b', color: '#64748b', borderRadius: '20px', padding: '8px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
                    View live feed while you wait →
                  </button>
                </div>
              )
            )}

            {/* LIVE FEED VIEW */}
            {view === 'live' && filtered.length > 0 && (
              <>
                {hero && (
                  <div id={`article-${articleId(hero.title || '')}`} style={highlightId === articleId(hero.title || '') ? { outline: '2px solid #38bdf8', borderRadius: '14px' } : {}}>
                    <HeroCard article={hero} />
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {rest.map((a, i) => {
                    const aid = articleId(a.title || '');
                    return (
                      <div key={a.link || i} id={`article-${aid}`} style={highlightId === aid ? { outline: '2px solid #38bdf8', borderRadius: '14px' } : {}}>
                        <NewsCard article={a} rank={i + 2} />
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {view === 'live' && filtered.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#475569' }}>
                No headlines match. <button onClick={() => { setSearch(''); setCategory('all'); }} style={{ background: 'none', border: 'none', color: '#38bdf8', cursor: 'pointer', fontWeight: '600' }}>Clear filters</button>
              </div>
            )}

            {/* Bottom CTA */}
            <div style={{ marginTop: '48px', padding: '28px', background: 'linear-gradient(135deg, #0f1e3d, #0f1827)', border: '1px solid rgba(56,189,248,0.2)', borderRadius: '16px', textAlign: 'center' }}>
              <div style={{ fontSize: '22px', marginBottom: '10px' }}>📰</div>
              <h3 style={{ color: '#f8fafc', fontSize: '18px', fontWeight: '800', margin: '0 0 8px' }}>Want a daily digest instead?</h3>
              <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 18px' }}>The KnowFi Brief breaks down the 3 biggest stories every morning — with what it actually means for your money.</p>
              <a href="/brief" style={{ display: 'inline-block', background: '#38bdf8', color: '#060d1a', fontWeight: '800', fontSize: '14px', padding: '12px 28px', borderRadius: '10px', textDecoration: 'none' }}>
                Read Today's Brief →
              </a>
            </div>
          </>
        )}

        )}
      </div>
    </div>
  );
}
